import router from "@/router/index";
import { LOGIN_URL } from "@/config";
import { RouteRecordRaw } from "vue-router";
import { useUserStore } from "@/store/modules/user";
import { useAuthStore } from "@/store/modules/auth";
import { getMessage } from "@/utils/message";
// 引入 views 文件夹下所有 vue 文件
const modules = import.meta.glob("@/views/**/*.vue");

/**
 * @description 初始化动态路由
 */
export const initDynamicRouter = async () => {
  const userStore = useUserStore();
  const authStore = useAuthStore();
  try {
    // 1.获取菜单列表 && 按钮权限列表
    await authStore.getAuthMenuList();
    await authStore.getAuthButtonList();

    // 2.判断当前用户有没有菜单权限
    if (!authStore.authMenuListGet.length) {
      getMessage()?.warning?.("无权限访问：当前账号无任何菜单权限，请联系系统管理员！");
      // userStore.setToken("");
      sessionStorage.removeItem("token");
      router.replace(LOGIN_URL);
      return Promise.reject("No permission");
    }

    // 3.添加动态路由
    // 使用原始嵌套结构注册路由，但需要特殊处理有 children 的父路由
    const addRouteRecursive = (routeItem: any) => {
      // 创建路由项的副本，避免修改原始数据
      const route: any = { ...routeItem };
      
      // 处理组件路径
      if (route.component && typeof route.component == "string") {
        // import.meta.glob 返回的键格式可能是 "/src/views/..." 或 "src/views/..."
        const componentPath1 = "/src/views" + route.component + ".vue";
        const componentPath2 = "src/views" + route.component + ".vue";
        
        if (modules[componentPath1]) {
          route.component = modules[componentPath1];
        } else if (modules[componentPath2]) {
          route.component = modules[componentPath2];
        } else {
          // 尝试查找匹配的键
          const componentKey = route.component.replace(/^\//, ""); // 移除开头的 /
          const matchedKey = Object.keys(modules).find(key => 
            key.includes(componentKey) && key.endsWith(".vue")
          );
          
          if (matchedKey) {
            route.component = modules[matchedKey];
            console.log(`组件路径匹配成功: ${componentKey} -> ${matchedKey}`);
          } else {
            console.error(`组件路径不存在: ${componentPath1} 或 ${componentPath2}`, route);
            console.error(`可用的模块键（前20个）:`, Object.keys(modules).slice(0, 20));
            // 不设置 component，让路由注册失败，这样可以看到错误
          }
        }
      }
      
      // 如果有子路由，递归处理子路由
      if (route.children && route.children.length > 0) {
        const nestedChildren: any[] = []; // 嵌套子路由（相对路径）
        const independentRoutes: any[] = []; // 独立路由（绝对路径）
        
        route.children.forEach((child: any) => {
          // 如果子路由的路径是绝对路径（以 / 开头），将其作为独立路由注册
          if (child.path && child.path.startsWith("/")) {
            // 先递归处理子路由
            const processedChild = addRouteRecursive(child);
            // 作为独立路由注册
            independentRoutes.push(processedChild);
          } else {
            // 相对路径的子路由，保留在 children 中
            nestedChildren.push(addRouteRecursive(child));
          }
        });
        
        // 先注册所有独立路由
        independentRoutes.forEach(independentRoute => {
          if (independentRoute.meta?.isFull) {
            router.addRoute(independentRoute as unknown as RouteRecordRaw);
          } else {
            router.addRoute("layout", independentRoute as unknown as RouteRecordRaw);
          }
        });
        
        // 如果有嵌套子路由，保留在 children 中
        if (nestedChildren.length > 0) {
          route.children = nestedChildren;
          
          // 对于有嵌套 children 的父路由，如果它的 component 和某个子路由相同，
          // 我们需要删除父路由的 component，让子路由独立显示
          if (route.component) {
            const hasSameComponentChild = routeItem.children.some((child: any) => 
              child.component === routeItem.component && !child.path?.startsWith("/")
            );
            
            if (hasSameComponentChild) {
              delete route.component;
            }
          }
        } else {
          // 没有嵌套子路由，删除 children 属性
          delete route.children;
        }
      } else {
        // 如果没有子路由，删除 children 属性
        delete route.children;
      }
      
      // 注册路由
      if (route.meta?.isFull) {
        router.addRoute(route as unknown as RouteRecordRaw);
      } else {
        router.addRoute("layout", route as unknown as RouteRecordRaw);
      }
      
      return route;
    };
    
    // 使用原始嵌套菜单列表注册路由，保持父子关系
    authStore.authMenuListGet.forEach(item => {
      addRouteRecursive(item);
    });
  } catch (error) {
    // 当按钮 || 菜单请求出错时，重定向到登陆页
    sessionStorage.removeItem("token");

    router.replace(LOGIN_URL);
    return Promise.reject(error);
  }
};
